import { useState } from 'react';
import { api } from '../api.js';
import { Modal } from '../components/Modal.js';
import { centsToInput, eur, inputToCents, monthName, periodLabel } from '../format.js';
import { useApp } from '../state.js';

export function ProjectionPage() {
  const { balance, catalog, run } = useApp();
  const [adjust, setAdjust] = useState(false);
  if (!balance || !catalog) return <div className="skeleton" />;
  const horizon = balance.periods.filter((p) => p.inHorizon);
  const last = horizon[horizon.length - 1]!;
  const first = horizon[0]!;
  const vals = horizon.map((p) => balance.totals[p.period]!.accumulatedCents);
  const max = Math.max(1, ...vals.map((v) => Math.abs(v)));
  const savings = horizon.reduce((a, p) => a + balance.totals[p.period]!.resultCents, 0);
  const startAcc = balance.totals[first.period]!.accumulatedCents - balance.totals[first.period]!.resultCents;
  return (
    <div className="page">
      <div className="stats">
        <div className="stat hero">
          <small>Acumulado previsto · {monthName(last.period)} {last.period.slice(0, 4)}</small>
          <strong>{eur(balance.totals[last.period]!.accumulatedCents)}</strong>
          <small>Si se mantiene lo configurado</small>
        </div>
        <div className="stat">
          <small>Resultado {monthName(first.period)} – {monthName(last.period)}</small>
          <strong className={savings >= 0 ? 'positive' : 'negative'}>{eur(savings)}</strong>
          <small>Suma de los {horizon.length} meses del horizonte</small>
        </div>
        <div className="stat">
          <small>Inicio de {monthName(first.period)}</small>
          <strong>{eur(startAcc)}</strong>
          <small>Acumulado de partida ({eur(balance.openingBalanceCents)} al inicio de {monthName(balance.openingPeriod)})</small>
        </div>
        <div className="stat">
          <small>Horizonte móvil</small>
          <strong>{balance.horizon.months} meses</strong>
          <small>{balance.horizon.includesCurrent ? 'Mes actual + once siguientes' : 'Desde el mes siguiente'}</small>
        </div>
      </div>
      <div className="grid">
        <div className="panel">
          <h2>Cómo evoluciona el acumulado</h2>
          <div className="chart" role="img" aria-label={`Acumulado mes a mes: ${horizon.map((p, i) => `${p.shortLabel} ${eur(vals[i])}`).join(', ')}`}>
            {horizon.map((p, i) => (
              <div className="col" key={p.period}>
                <span className="number">{eur(vals[i])}</span>
                <div className={`mark${p.status === 'current' ? ' current' : ''}${vals[i]! < 0 ? ' neg' : ''}`} style={{ height: `${Math.max(6, (Math.abs(vals[i]!) / max) * 130)}px` }} />
                <small>{p.shortLabel}</small>
              </div>
            ))}
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Mes</th>
                  <th className="number">Ingresos</th>
                  <th className="number">Egresos</th>
                  <th className="number">Resto</th>
                  <th className="number">Acumulado</th>
                  <th>Explicación</th>
                </tr>
              </thead>
              <tbody>
                {horizon.map((p) => {
                  const t = balance.totals[p.period]!;
                  return (
                    <tr key={p.period}>
                      <td>{p.label}</td>
                      <td className="number">{eur(t.incomeCents)}</td>
                      <td className="number">{eur(t.expenseCents)}</td>
                      <td className={`number ${t.resultCents >= 0 ? 'positive' : 'negative'}`}>{eur(t.resultCents)}</td>
                      <td className="number">{eur(t.accumulatedCents)}</td>
                      <td className="muted">
                        {t.actualExpenseCents ? `${eur(t.actualExpenseCents)} reales` : 'todo previsto'}
                        {t.pendingOutCents ? ` · ${eur(t.pendingOutCents)} sin clasificar` : ''}
                        {t.financingCents ? ` · ${eur(t.financingCents)} de préstamo sumados al acumulado` : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="panel">
          <h2>Ajustar una previsión</h2>
          <p className="muted">Un ajuste de un solo mes cambia la dotación o previsión de ese concepto sólo en ese mes y recalcula los acumulados posteriores. Para cambiar desde una fecha en adelante, usar nuevas condiciones en Conceptos.</p>
          <button type="button" className="button primary" onClick={() => setAdjust(true)}>
            Ajustar un mes
          </button>
          <h3 style={{ marginTop: 24 }}>Ajustes vigentes</h3>
          {catalog.overrides.length === 0 ? <p className="muted">Ninguno.</p> : null}
          {catalog.overrides.map((o) => {
            const c = catalog.concepts.find((x) => x.id === o.conceptId);
            return (
              <div className="row" key={o.id}>
                <div>
                  {c?.name} · {periodLabel(o.period)}
                  {o.note ? <div className="muted">{o.note}</div> : null}
                </div>
                <div className="inline">
                  <span className="number">{eur(o.amountCents)}</span>
                  <button type="button" className="button small" onClick={() => void run(() => api.deleteOverride(o.id), 'Ajuste eliminado')}>
                    Quitar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {adjust ? <AdjustModal onClose={() => setAdjust(false)} /> : null}
    </div>
  );
}

export function AdjustModal({ onClose, conceptId: preset }: { onClose: () => void; conceptId?: string }) {
  const { balance, catalog, run } = useApp();
  const [conceptId, setConceptId] = useState(preset ?? '');
  const [period, setPeriod] = useState(balance?.currentPeriod ?? '');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const row = balance?.rows.find((r) => r.conceptId === conceptId);
  const current = row?.cells[period]?.expectedCents ?? null;
  async function save() {
    const cents = inputToCents(amount);
    if (!conceptId) return setError('Elegí un concepto.');
    if (cents === null || cents < 0) return setError('Importe inválido.');
    const ok = await run(() => api.addOverride(conceptId, { period, amountCents: cents, note: note || null }), 'Previsión ajustada y acumulados recalculados');
    if (ok) onClose();
  }
  return (
    <Modal title="Ajustar un mes" onClose={onClose} error={error} actions={<button type="button" className="button primary" onClick={() => void save()}>Aplicar y recalcular</button>}>
      <label>
        Concepto
        <select data-autofocus value={conceptId} onChange={(e) => setConceptId(e.target.value)}>
          <option value="">Elegir…</option>
          {catalog?.concepts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <div className="field-row">
        <label>
          Mes
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {balance?.periods.map((p) => (
              <option key={p.period} value={p.period}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Nuevo importe previsto (€)
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={current !== null ? centsToInput(current) : '0,00'} />
        </label>
      </div>
      {current !== null ? <p className="muted">Previsto actualmente: {eur(current)}.</p> : null}
      <label>
        Motivo (opcional)
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
    </Modal>
  );
}
